/**
 * features/reports/useGeneratedBatches.ts
 * ------------------------------------------------------------------
 * Live list of the report BATCHES already generated for a given year+term
 * (one doc per class in reportBatches). Powers the "already generated —
 * who & when" lists in Generate / View / Send Reports so staff can see at
 * a glance which classes are done without re-selecting each one.
 * ------------------------------------------------------------------ */
import { useEffect, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { COLLECTIONS } from '@/contexts/DataContext';
import { compareClasses } from '@/lib/classSort';
import { getFormLevel } from '@/lib/grading';
import type { ReportBatch } from '@/types';

const asClass = (name: string) => ({ form: getFormLevel(name) ?? '', name });

export function useGeneratedBatches(year: string, term: string) {
  const [batches, setBatches] = useState<ReportBatch[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const unsub = onSnapshot(
      collection(db, COLLECTIONS.reportBatches),
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }) as ReportBatch)
          .filter((b) => b.year === year && b.term === term)
          // Order Form 1A → A2 June, using the batch's className.
          .sort((a, b) => compareClasses(asClass(a.className), asClass(b.className)));
        setBatches(rows);
        setLoading(false);
      },
      () => setLoading(false),
    );
    return () => unsub();
  }, [year, term]);

  return { batches, loading };
}
